import { Icon } from "@mdi/react";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useMutation } from "@apollo/client";
import { Business, Group, Person } from "@material-ui/icons";
import { mdiCheck, mdiContentSave, mdiContentSavePlus } from "@mdi/js";
import { Tooltip } from "antd";
import { SYSTEM_DEFAULT_FILTERS } from "constants/filter.constants";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { ProjectModel } from "api/project";
import { QUERY_SAVED_FILTERS, SAVE_FILTER } from "api/savedFilters";

import { LegendItemModel } from "models/LegendItem";

import { BaseButton, BaseInput, BaseMenu } from "components/base";
import { IconSpinner } from "components/icons";

import { EntityKind } from "../../models/entityKind";

interface SaveActiveFilterParams {
  project: ProjectModel;
  entityKind: EntityKind;
  filterName: string;
  isFilterModified: boolean;
  isFiltering: boolean;
  checkedLegendItems: React.MutableRefObject<LegendItemModel[]>;
  onSaveFilter: (filterName: string) => void;
}

let componentFilterName = "";

enum SaveState {
  Idle = "idle",
  Updating = "updating",
  Creating = "creating",
  UpdateSuccess = "update-success",
  CreateSuccess = "create-success"
}

export default function SaveActiveFilter(props: SaveActiveFilterParams) {
  const [filterName, setName] = useState(props.filterName);
  const [saveState, setSaveState] = useState<SaveState>(SaveState.Idle);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(props.isFiltering);

  const [saveFilter] = useMutation(SAVE_FILTER);
  const [nameError, setNameError] = useState("");

  // debounce button disabling to avoid flickering
  const enableButtons = useDebouncedCallback(() => {
    setIsButtonsDisabled(false);
  }, 500);

  const filterState = useSelector((state: RootState) => {
    const obj = {
      ...(state.filter.polygonFilter?.source && {
        polygonFilter:
          props.entityKind == EntityKind.Well
            ? state.filter.polygonFilter
            : state.facilityFilter.polygonFilter
      }),
      excludePolygonsFilter:
        props.entityKind == EntityKind.Well
          ? state.filter.excludePolygonsFilter
          : state.facilityFilter.excludePolygonsFilter,
      wellListFilter:
        props.entityKind == EntityKind.Well
          ? state.filter.wellListFilter
          : state.facilityFilter.wellListFilter,
      propertiesFilter:
        props.entityKind == EntityKind.Well
          ? state.filter.propertiesFilter
          : state.facilityFilter.propertiesFilter,
      entityKind: props.entityKind
    };
    return obj;
  });
  const savedFilters = useSelector((state: RootState) =>
    props.entityKind == EntityKind.Well
      ? state.filter.savedFilters
      : state.facilityFilter.savedFilters
  );
  const projectIdRef = useRef(props.project.projectId);

  const canUpdateFilter =
    props.filterName && !SYSTEM_DEFAULT_FILTERS.includes(props.filterName.toLowerCase());

  useEffect(() => {
    projectIdRef.current = props.project.projectId;
  }, [props.project]);

  useEffect(() => {
    setName(props.filterName || "");
  }, [props.filterName]);

  useEffect(() => {
    if (props.isFiltering) {
      setIsButtonsDisabled(true);
      enableButtons.cancel();
    } else {
      enableButtons();
    }
  }, [props.isFiltering, enableButtons]);

  const handleUpdateFilter = async () => {
    if (!projectIdRef.current || !canUpdateFilter) {
      return;
    }

    setSaveState(SaveState.Updating);

    const data = {
      projectID: projectIdRef.current,
      filterJSON: JSON.stringify(filterState),
      name: props.filterName,
      entityKind: props.entityKind
    };

    await saveFilter({
      variables: {
        input: data
      },
      context: {
        clientName: "saved-filters"
      },
      refetchQueries: [
        {
          query: QUERY_SAVED_FILTERS,
          variables: { req: { projectID: projectIdRef.current } },
          context: { clientName: "saved-filters" }
        }
      ],
      onCompleted: () => {
        setSaveState(SaveState.UpdateSuccess);
        props.onSaveFilter(props.filterName);

        setTimeout(() => {
          setSaveState(SaveState.Idle);
        }, 2000);
      },
      onError: () => {
        setSaveState(SaveState.Idle);
        setNameError("Failed to update filter. Please try again.");
      }
    });
  };

  const handleSaveAsNew = async (closeMenu?: () => void) => {
    if (!projectIdRef.current || componentFilterName === "") {
      return;
    }

    // Prevent overwriting system default filters
    if (SYSTEM_DEFAULT_FILTERS.includes(componentFilterName.toLowerCase())) {
      setNameError("You cannot overwrite a default filter.");
      return;
    }

    const isDuplicate = savedFilters.some(
      (filter) => filter.name.toLowerCase() === componentFilterName.toLowerCase()
    );
    if (isDuplicate) {
      setNameError("Duplicate name detected.");
      return;
    }

    setNameError("");

    setSaveState(SaveState.Creating);

    const data = {
      projectID: projectIdRef.current,
      filterJSON: JSON.stringify(filterState),
      name: componentFilterName,
      entityKind: props.entityKind
    };

    await saveFilter({
      variables: {
        input: data
      },
      context: {
        clientName: "saved-filters"
      },
      refetchQueries: [
        {
          query: QUERY_SAVED_FILTERS,
          variables: { req: { projectID: projectIdRef.current } },
          context: { clientName: "saved-filters" }
        }
      ],
      onCompleted: () => {
        setNameError("");
        setSaveState(SaveState.CreateSuccess);
        props.onSaveFilter(componentFilterName);
        closeMenu && closeMenu();

        setTimeout(() => {
          setSaveState(SaveState.Idle);
        }, 2000);
      },
      onError: () => {
        setSaveState(SaveState.Idle);
        setNameError("Failed to save filter. Please try again.");
      }
    });
  };

  const onEnterKey = async (ev, closeMenu?: () => void) => {
    if (ev.key === "Enter" && projectIdRef.current && componentFilterName !== "") {
      await handleSaveAsNew(closeMenu);
    }
  };

  useEffect(() => {
    componentFilterName = filterName;
  }, [filterName]);

  return (
    <IconsWrapper>
      <Tooltip
        placement="top"
        title={!canUpdateFilter ? "No changes to save" : `Save "${props.filterName}"`}>
        <IconButton
          data-testid="update-filter-button"
          onClick={handleUpdateFilter}
          disabled={isButtonsDisabled || !canUpdateFilter}>
          {saveState === SaveState.UpdateSuccess && <Icon path={mdiCheck} size={1} />}
          {saveState !== SaveState.Updating && saveState !== SaveState.UpdateSuccess && (
            <Icon path={mdiContentSave} size={1} />
          )}
          {saveState === SaveState.Updating && <IconSpinner size={14} padding={0} />}
        </IconButton>
      </Tooltip>

      <BaseMenu
        trigger={
          <Tooltip placement="top" title={props.filterName ? "Save as" : "Save Filter"}>
            <IconButton data-testid="create-filter-button" disabled={isButtonsDisabled}>
              {saveState === SaveState.CreateSuccess && <Icon path={mdiCheck} size={1} />}
              {saveState !== SaveState.Creating &&
                saveState !== SaveState.CreateSuccess && (
                  <Icon path={mdiContentSavePlus} size={1} />
                )}
              {saveState === SaveState.Creating && <IconSpinner size={14} padding={0} />}
            </IconButton>
          </Tooltip>
        }>
        {({ closeMenu }) => (
          <>
            <FilterHeaderWrapper className="align-left">
              {props.project.projectType === "Organization" ? (
                <Business style={{ color: "#A2AAAD", fontSize: "18px", top: "2px" }} />
              ) : props.project.projectType === "Shared" ||
                (!props.project.projectType && props.project.public) ? (
                <Group style={{ color: "#A2AAAD", fontSize: "18px", top: "2px" }} />
              ) : (
                <Person style={{ color: "#A2AAAD", fontSize: "18px", top: "2px" }} />
              )}
              {props.project.projectName}
            </FilterHeaderWrapper>
            <InputWrapper>
              <BaseInput
                value={filterName}
                autoFocus
                type="text"
                onChange={setName}
                onKeyDown={(evt) => onEnterKey(evt, closeMenu)}
                placeholder="Filter Name"></BaseInput>
              <BaseButton
                appearance="primary"
                data-testid="save-new-filter-button"
                onClick={() => handleSaveAsNew(closeMenu)}>
                Save
              </BaseButton>
            </InputWrapper>
            {nameError && <ErrorComponent>{nameError}</ErrorComponent>}
          </>
        )}
      </BaseMenu>
    </IconsWrapper>
  );
}

const IconsWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #b5b5b5;
  transition: color 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: var(--color-primary);
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    fill: currentColor;
    display: block;
  }
`;

const InputWrapper = styled.div`
  padding: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 5px;

  input {
    min-height: 32px;
  }

  .error-message {
    margin-top: 0;
  }

  .base-button {
    padding: 5px;
    margin: 2px;
  }
`;
const FilterHeaderWrapper = styled.div`
  padding: 5px 10px;
  display: flex;
  flex-direction: row;
  align-items: left;
  justify-content: left;
  gap: 5px;
`;

const ErrorComponent = styled.div`
  color: red;
  width: 100%;
  max-width: fit-content;
  text-align: left;
  padding-left: 5px;
  padding-right: 5px;
`;
